import { useContext, useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { Button, ButtonGroup, Table, ToggleButton } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import '../preview.scss'
import { setPageSize } from 'utils/util'
import { ResultCover, ResultSummary, ResultV2, Symptom } from '../result_v2'
import {
    BreastTestResultV2Form,
    OvaryTestResultV2Form,
    TestResultV2Form,
    useFetchTestResultV2Api,
    LungTestResultV2Form,
    StomachTestResultV2Form,
    useFetchPlotImageApi,
    PancreasTestResultV2Form,
    EsophagusTestResultV2Form,
    ColorectalTestResultV2Form,
} from 'api/result'
import { CheckboxField, InputField, RangeField, SelectField } from 'components/shared/input'
import { css } from '@emotion/css'
import { useFetchProductItemsApi } from 'api/product_item'
import { ProductItem } from 'entities/product_item'
import { OvaryTestView } from './ovary_test_view'
import { ResultV2ViewModel } from 'view_models/result_v2/result_v2_view_model'
import { BreastTestView } from './breast_test_view'
import axios from 'axios'
import { LungTestView } from './lung_test_view'
import { StomachTestView } from './stomach_test_view'
import { mapKeysSnakeCase } from 'utils/network/axios'
import { ColorectalTestView } from './colorectal_test_view'
import { EsophagusTestView } from './esophagus_test_view'
import { PancreasTestView } from './pancreas_test_view'
import { SYMPTOMS } from '../preview_results'

type Props = {
    history: H.History
}

const TestResultV2 = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const initial: OvaryTestResultV2Form &
        BreastTestResultV2Form &
        LungTestResultV2Form &
        StomachTestResultV2Form &
        ColorectalTestResultV2Form &
        EsophagusTestResultV2Form &
        PancreasTestResultV2Form = {
        baseScore: 0.5,
        age: 50,
        height: 160,
        weight: 50,
        birthCount: 1,
        takingPillYears: 0,
        endometriosis: false,
        breastCancer: false,
        familyBreast: false,
        familyOvary: false,
        enabled: false,
        firstDegreeRelative: 0,
        secondDegreeOrHigher: 0,
        mammography: false,
        diabetes: false,
        ageAtFirstBirth: 0,
        bloodType: 'A',
    }
    const form = useForm<TestResultV2Form>({
        ...initial,
        enabled: true,
        deliveryDate: '2022-07-01',
        markerCts: {},
        gender: 'MALE',
        personaHigh: 0.75,
        personaMiddle: 0.5,
        useCache: true,
        highPersonalRiskMagnification: 3,
        upperLimitOfPersonalRatio: 1000,
        lowerLimitOfPersonalRatio: 0,
    })
    const formPrev = useForm<TestResultV2Form>({ ...initial, deliveryDate: '2022-06-01', gender: 'MALE' })
    const formPrev2 = useForm<TestResultV2Form>({ ...initial, deliveryDate: '2022-05-01', gender: 'MALE' })
    const api = useFetchTestResultV2Api()
    const plotImageApi = useFetchPlotImageApi()
    const productItemsApi = useFetchProductItemsApi()
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()
    const [inspectionMethod, setInspectionMethod] = useState<'PCR' | 'NGS'>('PCR')

    const [symptoms, setSymptoms] = useState<Symptom[]>([])

    useEffect(() => {
        setSymptoms(SYMPTOMS)
    }, [])

    useEffect(() => {
        productItemsApi.execute()
    }, [])

    useEffect(() => {
        setPageSize('portrait')
    }, [])

    const getProductItem = (): ProductItem | undefined => {
        if (productItemsApi.response.results.length > 0) {
            return productItemsApi.response.results?.find((pi) => pi.id === form.object.productItemId)
        }
    }

    useEffectSkipFirst(() => {
        if (form.object.productItemId) {
            const markerCts: { [key: string]: number } = {}
            const hkCts: { [key: string]: number } = {}
            getProductItem()!.currentProtocolVersion?.protocolVersionProbes?.forEach((pvp) => {
                markerCts[pvp.probe!.name!] = 20
            })
            getProductItem()!
                .qcVersion?.protocolVersionProbes?.filter((pvp) => pvp.qcType === 'HK')
                .forEach((pvp) => {
                    hkCts[pvp.probe!.name!] = 20
                })
            form.update((f) => {
                f.markerCts = markerCts
                f.hkCts = hkCts
                f.symptoms = undefined
            })
            formPrev.update((f) => {
                f.markerCts = markerCts
                f.hkCts = hkCts
                f.symptoms = undefined
            })
            formPrev2.update((f) => {
                f.markerCts = markerCts
                f.hkCts = hkCts
                f.symptoms = undefined
            })
        }
    }, [form.object.productItemId])

    useEffectSkipFirst(() => {
        if(!form.getValue('productItemId')) {
            return
        }
        if (timeoutId) {
            clearTimeout(timeoutId)
        }
        const ti = setTimeout(() => {
            api.execute(
                inspectionMethod
                ,{
                current: form.object,
                previous: formPrev.object,
                previous2: formPrev2.object,
            })
        }, 100)
        setTimeoutId(ti)
    }, [form.object, formPrev.object, formPrev2.object, inspectionMethod])

    useEffectSkipFirst(() => {
        globalState.setLoading(plotImageApi.loading)
    }, [plotImageApi.loading])

    return (
        <>
            {/* <Flex className="no-print" style={{ marginLeft: 20 }} alignItems="flex-end">
                <div style={{ padding: 15 }}>
                    <Button
                        style={{ width: 200 }}
                        onClick={() => {
                            window.print()
                        }}
                    >
                        印刷プレビュー
                    </Button>
                </div>
            </Flex> */}
            <Flex>
                <div className="no-print" style={{ width: 350, fontSize: 12 }}>
                    <div style={{ padding: 10, backgroundColor: 'white', marginTop: 10, height: '95vh', overflow: 'scroll' }}>
                        {productItemsApi.response.results.length > 0 && (
                            <>
                                <ButtonGroup>
                                    <ToggleButton
                                        id={`radio-pcr`}
                                        disabled={api.loading}
                                        type="radio"
                                        variant={'outline-primary'}
                                        name="inspectionMethod"
                                        value={'pcr'}
                                        checked={inspectionMethod === 'PCR'}
                                        onChange={(e) => setInspectionMethod('PCR')}
                                    >
                                        PCR
                                    </ToggleButton>
                                    <ToggleButton
                                        id={`radio-ngs`}
                                        disabled={api.loading}
                                        type="radio"
                                        variant={'outline-primary'}
                                        name="inspectionMethod"
                                        value={'ngs'}
                                        checked={inspectionMethod === 'NGS'}
                                        onChange={(e) => setInspectionMethod('NGS')}
                                    >
                                        NGS
                                    </ToggleButton>
                                </ButtonGroup>
                                <SelectField
                                    form={form}
                                    attr={'productItemId'}
                                    selectItems={productItemsApi.response.results.map((pi) => {
                                        return { label: pi.name, value: pi.id! }
                                    })}
                                    label="製品"
                                    includeBlank
                                />
                                <CheckboxField form={form} attr="enablePlotImage" checkboxLabel="Plot画像を出力" />
                                {form.object.enablePlotImage && (
                                    <div style={{ marginBottom: 10, padding: 10, backgroundColor: '#ddd8' }}>
                                        <Flex>
                                            <RangeField form={form} attr="personaMiddle" min={0} max={1} step={0.01} showValue label="MLScore(中)" />
                                            <RangeField form={form} attr="personaHigh" min={0} max={1} step={0.01} showValue label="MLScore(高)" />
                                        </Flex>
                                        <RangeField
                                            form={form}
                                            attr="highPersonalRiskMagnification"
                                            min={0}
                                            max={20}
                                            step={0.1}
                                            showValue
                                            label="パーソナル(高)の倍率"
                                        />
                                        <InputField
                                            form={form}
                                            attr="upperLimitOfPersonalRatio"
                                            type="number"
                                            label="パーソナルリスクレートの上限"
                                            step={0.1}
                                        />
                                        <InputField
                                            form={form}
                                            attr="lowerLimitOfPersonalRatio"
                                            type="number"
                                            label="パーソナルリスクレートの下限"
                                            step={0.1}
                                        />
                                        <CheckboxField
                                            form={form}
                                            attr="useCache"
                                            label="PPVキャッシュを利用"
                                            hint="年齢を変更した場合はキャッシュを利用しないでください。"
                                        />
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                plotImageApi.execute(inspectionMethod, {
                                                    json: JSON.stringify(mapKeysSnakeCase(form.object)),
                                                })
                                            }}
                                            style={{ width: '100%' }}
                                        >
                                            Plot Image
                                        </Button>
                                    </div>
                                )}
                                {!['ovary', 'breast'].includes(getProductItem()?.nameEn ?? '') && (
                                    <SelectField
                                        form={form}
                                        attr={'gender'}
                                        selectItems={[
                                            { label: '男性', value: 'MALE' },
                                            { label: '女性', value: 'FEMALE' },
                                        ]}
                                        label="性別"
                                    />
                                )}
                                <CheckboxField form={form} attr="useCt" checkboxLabel="CTから出力する" />
                            </>
                        )}

                        <div style={{ padding: 10 }}>
                            <div>検品ステータス</div>

                            <Flex style={{ fontSize: 14 }} flexWrap="wrap">
                                <CheckboxField form={form} attr={'includeBlood'} checkboxLabel="血液混入" />
                                <CheckboxField form={form} attr={'overTemperature'} checkboxLabel="温度逸脱" />
                                <CheckboxField form={form} attr={'differentContainer'} checkboxLabel="指定容器以外" />
                                <CheckboxField form={form} attr={'nonUrine'} checkboxLabel="尿以外の検体" />
                                <CheckboxField form={form} attr={'quantityStatusNg'} checkboxLabel="尿量が1ml以下の検体" />
                            </Flex>
                        </div>

                        {getProductItem()?.nameEn === 'ovary' && (
                            <OvaryTestView form={form} formPrev={formPrev} formPrev2={formPrev2} productItem={getProductItem()!} symptoms={symptoms} />
                        )}

                        {getProductItem()?.nameEn === 'breast' && (
                            <BreastTestView form={form} formPrev={formPrev} formPrev2={formPrev2} productItem={getProductItem()!} symptoms={symptoms} />
                        )}

                        {getProductItem()?.nameEn === 'lung' && (
                            <LungTestView form={form} formPrev={formPrev} formPrev2={formPrev2} productItem={getProductItem()!} symptoms={symptoms} />
                        )}

                        {getProductItem()?.nameEn === 'stomach' && (
                            <StomachTestView form={form} formPrev={formPrev} formPrev2={formPrev2} productItem={getProductItem()!} symptoms={symptoms} />
                        )}
                        {getProductItem()?.nameEn === 'colorectal' && (
                            <ColorectalTestView form={form} formPrev={formPrev} formPrev2={formPrev2} productItem={getProductItem()!} symptoms={symptoms} />
                        )}
                        {getProductItem()?.nameEn === 'esophagus' && (
                            <EsophagusTestView form={form} formPrev={formPrev} formPrev2={formPrev2} productItem={getProductItem()!} symptoms={symptoms} />
                        )}
                        {getProductItem()?.nameEn === 'pancreas' && (
                            <PancreasTestView form={form} formPrev={formPrev} formPrev2={formPrev2} productItem={getProductItem()!} symptoms={symptoms} />
                        )}
                    </div>
                </div>
                <div
                    className={css`
                        @media screen {
                            height: 95vh;
                            overflow: scroll;
                        }
                    `}
                >
                    {api.response.result.background && api.response.sample.code && (
                        <>
                            <div style={{ margin: '5mm' }} className="no-print">
                                {(() => {
                                    const vm = new ResultV2ViewModel(api.response.result!, 'hogehoge', '', '', {})
                                    return (
                                        <>
                                            <CheckboxField form={form} attr="ppvMode" checkboxLabel="感度、特異度、PPVを確認" />
                                            {form.object.ppvMode ? (
                                                <>
                                                    <div style={{ width: 1000 }}>
                                                        <Table style={{ marginTop: 20 }} className="sticky-table">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: 40 }}></th>
                                                                    <th>ML Score</th>
                                                                    <th>感度</th>
                                                                    <th>特異度</th>
                                                                    <th>PPV</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {new Array(1000).fill(0).map((_, i) => {
                                                                    const roc = api.response.smoothRoc
                                                                    const index = 999 - i
                                                                    if (i % 10 !== 9) {
                                                                        return
                                                                    }
                                                                    return (
                                                                        <tr key={'roc-' + index}>
                                                                            <td>
                                                                                <input type="checkbox"></input>
                                                                            </td>
                                                                            <td>{Math.round((roc?.thresholds[index] ?? 0) * 1000) / 1000}</td>
                                                                            <td>{Math.round((roc?.sensitivity[index] ?? 0) * 1000000) / 10000}%</td>
                                                                            <td>{Math.round((roc?.specificity[index] ?? 0) * 1000000) / 10000}%</td>
                                                                            <td>{Math.round((roc?.rocScore[index] ?? 0) * 100000) / 1000}%</td>
                                                                        </tr>
                                                                    )
                                                                })}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <Flex>
                                                        <div>機械学習スコア: {api.response.result.mlScore}</div>
                                                    </Flex>
                                                    <Flex>
                                                        <div>miRNAスコア: {vm.miRnaScore()}</div>
                                                        <div>パーソナルスコア: {vm.personalScore()}</div>
                                                        <div>PPV`: {vm.info().score.probability}</div>
                                                    </Flex>
                                                    <Flex>
                                                        <div>PPV: {vm.info().score.ppv}</div>
                                                        <div>パーソナルリスク倍率: {vm.info().score.personal_risk_ratio}</div>
                                                    </Flex>
                                                    <Flex>
                                                        <div>PPV`: {vm.info().score.probability}</div>
                                                    </Flex>
                                                    <Flex>
                                                        <div>min: {Math.round(vm.info().score.min_personal_risk_ratio * 1000) / 1000}</div>
                                                        <div>mid: {Math.round(vm.info().score.middle_personal_risk_ratio * 1000) / 1000}</div>
                                                        <div>high: {Math.round(vm.info().score.high_personal_risk_ratio * 1000) / 1000}</div>
                                                        <div>max: {Math.round(vm.info().score.max_personal_risk_ratio * 1000) / 1000}</div>
                                                        <div>current: {Math.round(vm.info().score.personal_risk_ratio * 1000) / 1000}</div>
                                                    </Flex>
                                                    <Flex>
                                                        <div>プロットX: {vm.info().plot.x}</div>
                                                        <div>プロットy: {vm.info().plot.y}</div>
                                                    </Flex>
                                                </>
                                            )}
                                        </>
                                    )
                                })()}
                            </div>

                            {!form.object.ppvMode && (
                                <>
                                    <ResultCover sample={api.response.sample} />
                                    <ResultSummary
                                        sample={api.response.sample}
                                        results={[api.response.result]}
                                        symptoms={symptoms}
                                        productItems={productItemsApi.response.results}
                                    />
                                    <ResultV2 sample={api.response.sample} result={api.response.result} symptoms={symptoms} inspectionMethod={inspectionMethod} onlyResult />
                                </>
                            )}
                        </>
                    )}
                </div>
            </Flex>
        </>
    )
}

export default withRouter(TestResultV2)
